/**
 * Screen-local API module for the CTO Email stats report (task 035-03).
 *
 * Codes against task 035-02's pinned contract (`tasks/035_stats_screen/task_02_cto_stats_query_opus.md`
 * §Contract, lines 38-88) and task 04's export endpoint (README decision 8 (c)).
 * Task 02 need not have landed — this module and its test mock the response
 * shape; the DTOs below are the api-local (A3) types copied verbatim from the
 * pinned contract.
 *
 * Deliberately outside `lib/api.ts` (ADR-010 gap #2 -> A2 (b)): same base-URL
 * expression and `{ data }` envelope handling as `reports-api.ts`.
 */

import { apiFetch } from '../../lib/api';

/** One traffic-source (site) row of `GET /admin/reports/cto-stats` (pinned contract). */
export interface CtoSourceRow {
  siteId: string; // site.id::text
  siteCode: string; // site.site_id -- her "Site ID"
  label: string; // site.label -- her "Traffic Source"
  leads: number; // lead rows through the source's links
  uniqueLeads: number; // COUNT(DISTINCT email_hash) across those links (Q82)
  success: number; // COUNT(DISTINCT email_hash) FILTER (status = 'success')
  duplicate: number; // ... 'duplicate'
  unchecked: number; // ... IS NULL
  rejected: number; // capture_rejection rows through the source's links
  newToFilePct: number | null; // success / uniqueLeads * 100, 2 dp; null when uniqueLeads = 0
  rates: number[]; // distinct non-null link prices in the selection, ascending
  hasUnpricedLink: boolean; // some link in the selection has price NULL
  costOfTotal: number | null; // Sum over links (price x leads), NULL-priced links contribute 0; null when every price is null
  costOfUnique: number | null; // (rates.length === 1 && !hasUnpricedLink) ? rate x uniqueLeads
  //   : Sum over links (price x link unique), NULL-priced links contribute 0;
  // null when every price is null
  costOfUniqueMixed: boolean; // true when rates.length > 1 OR (hasUnpricedLink && rates.length > 0)
  //   -- the cell carries the "mixed / partly unpriced -- per-link sum" marker;
  //   an unpriced link must never be billed at a sibling's rate (README decision 6)
}

/** One link row of `GET /admin/reports/cto-stats` (pinned contract). */
export interface CtoLinkRow {
  linkId: string;
  siteId: string;
  campaignId: string;
  campaignName: string;
  promoTypeId: string;
  promoTypeName: string;
  linkStatus: 'active' | 'inactive';
  price: number | null;
  leads: number;
  uniqueLeads: number;
  success: number;
  duplicate: number;
  unchecked: number;
  rejected: number;
  newToFilePct: number | null;
  costOfTotal: number | null; // price x leads
  costOfUnique: number | null; // price x uniqueLeads
}

/** Grand totals over the whole selection (pinned contract): distinct counts recomputed, sums summed. */
export interface CtoTotals {
  leads: number;
  uniqueLeads: number;
  success: number;
  duplicate: number;
  unchecked: number;
  rejected: number;
  newToFilePct: number | null;
  costOfTotal: number | null; // Sum of the source rows' costOfTotal (= Sum over links)
  costOfUnique: number | null; // Sum of the SOURCE rows' costOfUnique -- never Sum over links, never
  // a selection-wide rate x total unique (the rows are the unit she pays)
}

export interface CtoStatsResponse {
  from: string;
  to: string;
  timezone: string;
  includeTest: boolean;
  sources: CtoSourceRow[]; // ORDER BY label, siteCode
  links: CtoLinkRow[]; // ORDER BY site label, campaignName, promoTypeName, linkId
  totals: CtoTotals;
}

/**
 * Screen filter state. `campaignId === ''` means the filter is off and is
 * omitted from the query string; `from`/`to`/`includeTest` are always sent
 * (co-op's `CoopCountsFilters` convention, `reports-api.ts`).
 */
export interface CtoStatsFilters {
  from: string;
  to: string;
  includeTest: boolean;
  campaignId: string;
}

/** CTO leads export scope + variant (task 04's endpoint, README decision 8 (c)). */
export type CtoLeadsVariant = 'all' | 'unique' | 'rejected';

export interface CtoLeadsExportParams {
  from: string;
  to: string;
  includeTest: boolean;
  siteId: string;
  linkId?: string;
  campaignId?: string;
  variant: CtoLeadsVariant;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const ADMIN_PREFIX = '/admin';

/** `GET /admin/reports/cto-stats` query -- `coopReportQuerySchema.pick({ from, to, includeTest, campaignId })`. */
function buildCtoStatsQuery(filters: CtoStatsFilters): URLSearchParams {
  const params = new URLSearchParams();
  params.set('from', filters.from);
  params.set('to', filters.to);
  params.set('includeTest', filters.includeTest ? 'true' : 'false');
  if (filters.campaignId) params.set('campaignId', filters.campaignId);
  return params;
}

/** Absolute CTO stats URL, ready for `fetch`. */
export function ctoStatsUrl(filters: CtoStatsFilters): string {
  return `${API_BASE_URL}${ADMIN_PREFIX}/reports/cto-stats?${buildCtoStatsQuery(filters).toString()}`;
}

/**
 * Absolute `cto-leads.csv` export URL for `window.open` -- param order matches
 * task 03's spec verbatim: `from&to&includeTest&siteId[&linkId][&campaignId]&variant`.
 */
export function ctoLeadsExportUrl(params: CtoLeadsExportParams): string {
  const query = new URLSearchParams();
  query.set('from', params.from);
  query.set('to', params.to);
  query.set('includeTest', params.includeTest ? 'true' : 'false');
  query.set('siteId', params.siteId);
  if (params.linkId) query.set('linkId', params.linkId);
  if (params.campaignId) query.set('campaignId', params.campaignId);
  query.set('variant', params.variant);
  return `${API_BASE_URL}${ADMIN_PREFIX}/reports/cto-leads.csv?${query.toString()}`;
}

/**
 * `message` of a Nest error body without a cast (`reports-api.ts`'s idiom):
 * narrow the unknown JSON with the `in` operator, then the string check.
 */
function messageFromBody(body: unknown, fallback: string): string {
  if (typeof body === 'object' && body !== null && 'message' in body) {
    const message = body.message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return fallback;
}

/** Success envelope -- matches the api's `ApiResponse<T>` (conventions §DTO). */
interface CtoStatsEnvelope {
  data: CtoStatsResponse;
}

/** Fetch the CTO stats for one filter set. Non-2xx throws with the body's message (or the status text). */
export async function fetchCtoStats(filters: CtoStatsFilters): Promise<CtoStatsResponse> {
  const response = await apiFetch(ctoStatsUrl(filters));
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => ({}));
    throw new Error(messageFromBody(body, response.statusText));
  }
  const envelope: CtoStatsEnvelope = await response.json();
  return envelope.data;
}
